/**
 * LCH Macau 龍昌行 Shopify Theme JS
 * Handles: Header mobile nav, Cart Drawer, Search Modal,
 *          Product Gallery, Hero Carousel, Quantity Selectors,
 *          Product Options, Add to Cart (AJAX)
 */

/* ============================================================
   UTILITIES
   ============================================================ */
const $ = (selector, context = document) => context.querySelector(selector);
const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return `MOP ${amount}`;
}

/* ============================================================
   MOBILE NAV
   ============================================================ */
class MobileNav {
  constructor() {
    this.toggle = $('#mobile-nav-toggle');
    this.nav = $('#mobile-nav');
    this.overlay = $('#mobile-nav-overlay');
    this.close = $('#mobile-nav-close');
    if (!this.toggle || !this.nav) return;
    this.init();
  }

  init() {
    this.toggle.addEventListener('click', () => this.open());
    this.close?.addEventListener('click', () => this.closeNav());
    this.overlay?.addEventListener('click', () => this.closeNav());

    // Sub-menu toggles
    $$('.mobile-nav__sub-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const subList = btn.nextElementSibling;
        if (!subList) return;
        const isOpen = subList.style.display === 'block';
        subList.style.display = isOpen ? 'none' : 'block';
        btn.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  open() {
    this.nav.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.close?.focus();
  }

  closeNav() {
    this.nav.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
    this.toggle?.focus();
  }
}

/* ============================================================
   CART DRAWER
   ============================================================ */
class CartDrawer {
  constructor() {
    this.drawer = $('#cart-drawer');
    this.overlay = $('#cart-overlay');
    this.openBtns = $$('[data-cart-open]');
    this.closeBtn = $('#cart-drawer-close');
    if (!this.drawer) return;
    this.init();
  }

  init() {
    this.openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    }));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Delegate qty changes and removes
    this.drawer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-qty]');
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (btn) this.updateQty(btn.dataset.key, btn.dataset.cartQty);
      if (removeBtn) this.removeItem(removeBtn.dataset.key);
    });
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  render(cart) {
    // Update count badges
    $$('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('site-header__cart-count--hidden', cart.item_count === 0);
    });

    const body = $('#cart-drawer-body');
    const footer = $('#cart-drawer-footer');
    const subtotalEl = $('#cart-drawer-subtotal');
    if (!body) return;

    if (footer) footer.classList.toggle('cart-drawer__footer--hidden', cart.item_count === 0);
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>您的購物車是空的</p>
          <a href="/collections/all" class="btn btn--primary btn--small" onclick="window.cartDrawer?.close()">繼續購物</a>
        </div>`;
      return;
    }

    // Shipping progress
    const freeShippingThreshold = 20000; // MOP 200 in cents
    const progress = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);
    const remaining = Math.max(0, freeShippingThreshold - cart.total_price);
    const shippingMsg = remaining > 0
      ? `再消費 ${formatMoney(remaining)} 即享免費送貨`
      : '🎉 您已達到免費送貨門檻！';

    const itemsHtml = cart.items.map(item => `
      <div class="cart-drawer__item">
        <a href="${item.url}" class="cart-drawer__item-image-link">
          <img src="${item.image}" alt="${item.title}" width="72" height="72" loading="lazy">
        </a>
        <div class="cart-drawer__item-details">
          <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          <div class="cart-drawer__item-bottom">
            <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
            <div style="display:flex;align-items:center;gap:0.375rem;">
              <button type="button" class="cart-drawer__qty-btn" data-cart-qty="decrease" data-key="${item.key}" aria-label="減少數量">−</button>
              <span class="cart-drawer__qty-value">${item.quantity}</span>
              <button type="button" class="cart-drawer__qty-btn" data-cart-qty="increase" data-key="${item.key}" aria-label="增加數量">＋</button>
            </div>
          </div>
        </div>
        <button class="cart-drawer__remove" data-cart-remove data-key="${item.key}" aria-label="移除商品">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>
        </button>
      </div>`).join('');

    body.innerHTML = `
      <div class="cart-drawer__shipping-bar${progress >= 100 ? ' cart-drawer__shipping-bar--achieved' : ''}">
        <p class="cart-drawer__shipping-text">${shippingMsg}</p>
        <div class="cart-drawer__shipping-progress">
          <div class="cart-drawer__shipping-fill" style="width:${progress}%"></div>
        </div>
      </div>
      <div class="cart-drawer__items">${itemsHtml}</div>`;

  }

  async updateQty(key, action) {
    try {
      // Get current cart
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const newQty = action === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      this.refresh();
    } catch (err) {
      console.error('Update qty failed:', err);
    }
  }

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      this.refresh();
    } catch (err) {
      console.error('Remove item failed:', err);
    }
  }
}

/* ============================================================
   SEARCH MODAL
   ============================================================ */
class SearchModal {
  constructor() {
    this.modal = $('#search-modal');
    this.overlay = $('#search-modal-overlay');
    this.openBtns = $$('[data-search-open]');
    this.closeBtn = $('#search-modal-close');
    this.input = this.modal?.querySelector('.search-modal__input');
    if (!this.modal) return;
    this.init();
  }

  init() {
    this.openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    }));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.modal.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input?.focus(), 100);
  }

  close() {
    this.modal.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }
}

/* ============================================================
   HERO BANNER CAROUSEL
   ============================================================ */
class HeroCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.hero-banner__track');
    this.slides = $$('.hero-banner__slide', container);
    this.dots = $$('.hero-banner__dot', container);
    this.prevBtn = container.querySelector('.hero-banner__arrow--prev');
    this.nextBtn = container.querySelector('.hero-banner__arrow--next');
    this.current = 0;
    this.total = this.slides.length;
    this.autoplayInterval = null;
    if (this.total <= 1) return;
    this.init();
  }

  init() {
    this.prevBtn?.addEventListener('click', () => { this.goTo(this.current - 1); this.resetAutoplay(); });
    this.nextBtn?.addEventListener('click', () => { this.goTo(this.current + 1); this.resetAutoplay(); });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.goTo(i); this.resetAutoplay(); }));
    this.startAutoplay();

    // Touch support
    let startX = 0;
    this.container.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.container.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { this.goTo(diff > 0 ? this.current + 1 : this.current - 1); this.resetAutoplay(); }
    });
  }

  goTo(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    if (this.track) this.track.style.transform = `translateX(-${this.current * 100}%)`;
    this.dots.forEach((dot, i) => dot.classList.toggle('hero-banner__dot--active', i === this.current));
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => this.goTo(this.current + 1), 5000);
  }

  resetAutoplay() {
    clearInterval(this.autoplayInterval);
    this.startAutoplay();
  }
}

/* ============================================================
   PRODUCT GALLERY
   ============================================================ */
class ProductGallery {
  constructor(container) {
    this.container = container;
    this.mainImage = container.querySelector('.product-gallery__main-image');
    this.thumbnails = $$('.product-gallery__thumbnail', container);
    this.prevBtn = container.querySelector('.product-gallery__arrow--prev');
    this.nextBtn = container.querySelector('.product-gallery__arrow--next');
    this.images = this.thumbnails.map(t => t.querySelector('img')?.src);
    this.current = 0;
    if (!this.mainImage) return;
    this.init();
  }

  init() {
    this.thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });
    this.prevBtn?.addEventListener('click', () => this.goTo(this.current - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.current + 1));
  }

  goTo(index) {
    const total = this.images.length;
    this.current = ((index % total) + total) % total;
    if (this.mainImage && this.images[this.current]) {
      this.mainImage.src = this.images[this.current].replace(/width=\d+/, 'width=900');
    }
    this.thumbnails.forEach((t, i) => t.classList.toggle('product-gallery__thumbnail--active', i === this.current));
  }
}

/* ============================================================
   PRODUCT FORM — OPTIONS & AJAX ADD TO CART
   ============================================================ */
class ProductForm {
  constructor(form) {
    this.form = form;
    this.variantInput = form.querySelector('[name="id"]');
    this.optionBtns = $$('.product-form__option-btn', form);
    this.quantityInput = form.querySelector('[name="quantity"]');
    this.quantityBtns = $$('[data-product-quantity-action]', form);
    this.addToCartBtn = form.querySelector('.btn--add-to-cart');
    this.priceEl = form.closest('.product-info')?.querySelector('.product-info__price');
    this.variants = JSON.parse(document.getElementById('product-json')?.textContent || '{}').variants || [];
    this.init();
  }

  init() {
    this.quantityBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (!this.quantityInput) return;
        const current = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
        const next = btn.dataset.productQuantityAction === 'increase' ? current + 1 : Math.max(1, current - 1);
        this.quantityInput.value = next;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    this.quantityInput?.addEventListener('change', () => {
      this.quantityInput.value = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
    });

    this.optionBtns.forEach(btn => {
      const group = btn.closest('[data-option-name]');
      if (btn.classList.contains('product-form__option-btn--selected') && group) {
        group.dataset.selectedValue = btn.dataset.value;
      }
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-option-name]');
        if (!group) return;
        $$('.product-form__option-btn', group).forEach(b => b.classList.remove('product-form__option-btn--selected'));
        btn.classList.add('product-form__option-btn--selected');
        group.dataset.selectedValue = btn.dataset.value;
        this.updateVariant();
      });
    });

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.addToCart();
    });
  }

  updateVariant() {
    const selectedValues = $$('[data-option-name]', this.form).map(group => group.dataset.selectedValue);
    const match = this.variants.find(v => v.options.every((opt, i) => selectedValues[i] === opt));
    if (match && this.variantInput) {
      this.variantInput.value = match.id;
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !match.available;
        this.addToCartBtn.textContent = match.available ? '加入購物車' : '已售罄';
      }
    }
  }

  async addToCart() {
    if (!this.variantInput?.value || this.addToCartBtn?.disabled) return;
    const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);
    this.addToCartBtn.textContent = '加入中...';
    this.addToCartBtn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantInput.value, quantity: qty })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      window.cartDrawer?.refresh();
      window.cartDrawer?.open();
      this.addToCartBtn.textContent = '已加入 ✓';
      setTimeout(() => {
        this.addToCartBtn.textContent = '加入購物車';
        this.addToCartBtn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error(err);
      this.addToCartBtn.textContent = '加入購物車';
      this.addToCartBtn.disabled = false;
    }
  }
}

/* ============================================================
   PRODUCT ACCORDION
   ============================================================ */
function initAccordions() {
  $$('.product-accordion').forEach(details => {
    const summary = details.querySelector('.product-accordion__header');
    if (!summary) return;
    // details/summary is native, just ensure icon rotation works via CSS [open]
  });
}

/* ============================================================
   CART PAGE — QUANTITY UPDATES
   ============================================================ */
function initCartPage() {
  const form = $('#cart-form');
  if (!form) return;

  form.addEventListener('click', async (e) => {
    const btn = e.target.closest('.quantity-selector__btn');
    const removeBtn = e.target.closest('.cart-item__remove');

    if (btn) {
      e.preventDefault();
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const input = form.querySelector(`input[data-key="${key}"]`);
      if (!input) return;
      let qty = parseInt(input.value);
      qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
      input.value = qty;
      await updateCartItem(key, qty);
      return;
    }

    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.dataset.key;
      await updateCartItem(key, 0);
    }
  });
}

async function updateCartItem(key, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) throw new Error('Cart change failed');
    // Full reload keeps line totals, inventory state and checkout state in sync.
    window.location.reload();
  } catch (err) {
    console.error('Cart update failed:', err);
  }
}

/* ============================================================
   COLLECTION SIDEBAR FILTER TOGGLES
   ============================================================ */
function initSidebarToggles() {
  $$('.collection-sidebar__group-toggle').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const list = toggle.nextElementSibling;
      if (!list) return;
      const isOpen = list.style.display !== 'none';
      list.style.display = isOpen ? 'none' : 'flex';
      toggle.setAttribute('aria-expanded', !isOpen);
      const icon = toggle.querySelector('svg');
      if (icon) icon.style.transform = isOpen ? '' : 'rotate(180deg)';
    });
  });
}

/* ============================================================
   COLLECTION SORTING
   ============================================================ */
function initCollectionSort() {
  const sortBy = $('#sort-by');
  if (!sortBy) return;
  sortBy.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortBy.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
}

/* ============================================================
   STICKY HEADER SCROLL BEHAVIOUR
   ============================================================ */
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;
  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const current = window.scrollY;
    if (current > 80) {
      header.classList.add('site-header--scrolled');
    } else {
      header.classList.remove('site-header--scrolled');
    }
    lastScroll = current;
  }, { passive: true });
}

/* ============================================================
   HOMEPAGE / ABOUT / CONTACT — SCROLL REVEAL
   ============================================================ */
function initScrollReveal() {
  const isTargetPage = document.body.classList.contains('template-index')
    || document.body.classList.contains('template-page-about')
    || document.body.classList.contains('template-page-contact');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!isTargetPage || prefersReducedMotion) return;

  const sections = $$('main > .shopify-section');
  if (!sections.length) return;

  sections.forEach(section => section.classList.add('scroll-reveal'));
  document.documentElement.classList.add('scroll-reveal-ready');

  if (!('IntersectionObserver' in window)) {
    sections.forEach(section => section.classList.add('scroll-reveal--visible'));
    return;
  }

  const observer = new IntersectionObserver((entries, activeObserver) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('scroll-reveal--visible');
      activeObserver.unobserve(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });

  sections.forEach(section => observer.observe(section));
}

/* ============================================================
   INIT
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  // Global instances
  window.cartDrawer = new CartDrawer();
  window.searchModal = new SearchModal();
  new MobileNav();

  // Hero carousels
  $$('.hero-banner').forEach(el => new HeroCarousel(el));

  // Product galleries
  $$('.product-gallery').forEach(el => new ProductGallery(el));

  // Product forms
  $$('.product-form').forEach(el => new ProductForm(el));

  // Cart page
  initCartPage();

  // Sidebar
  initSidebarToggles();
  initCollectionSort();

  // Sticky header
  initStickyHeader();

  // Homepage, About and Contact reveal animation
  initScrollReveal();

  // Accordions
  initAccordions();
});
